// src/api/BaseAPI.ts
export interface RequestOptions extends RequestInit {
  auth?: boolean; // whether to include cookies automatically
}

export class ApiError extends Error {
  status: number;
  data?: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export class BaseAPI {
  constructor(private baseUrl: string = "") {}

  protected async request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const isFormData = options.body instanceof FormData;

    const defaultHeaders: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string> || {}),
    };

    const res = await fetch(url, {
      ...options,
      headers: defaultHeaders,
      credentials: options.auth ? "include" : options.credentials ?? "same-origin",
    });

    // Try to parse JSON either way (success or error)
    const contentType = res.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");

    if (!res.ok) {
      let data: any = undefined;
      let message = res.statusText;

      if (isJson) {
        try {
          data = await res.json();
          message = data?.message || message;
        } catch {
          // ignore parse error
        }
      } else {
        // Optional: read text for non-json errors
        try {
          const text = await res.text();
          if (text) message = text;
        } catch {
          // ignore
        }
      }

      throw new ApiError(res.status, message, data);
    }

    // Success path
    if (isJson) {
      return (await res.json()) as T;
    }

    // If you ever return non-json from the API:
    return (await res.text()) as unknown as T;
  }
}
